import { LayoutDashboard, Ship, Database, FolderTree, ChevronRight, Building2, MessageSquare, MapPin, Plug, Lock, Wallet, Sparkles, Activity } from "lucide-react";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { CreditUsage } from "@/components/CreditUsage";
import { OrganizationSwitcher } from "@/components/layout/OrganizationSwitcher";
import { UserProfileMenu } from "@/components/layout/UserProfileMenu";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { useEmbarques } from "@/hooks/useEmbarques";
import { useOrgQuota } from "@/hooks/useOrgQuota";
import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

import { Badge } from "@/components/ui/badge";

const LOGO_DARK = "https://kargonode.com/wp-content/uploads/2026/02/logo-correo.webp";
const LOGO_LIGHT = "https://kargonode.com/wp-content/uploads/2026/03/Manual-de-Marca-HUMAN-CLARITY-Pilar-Rodriguez-9.webp";
const ICON_DARK = "https://kargonode.com/wp-content/uploads/2026/02/cropped-Kargonode-fv-1-1.webp";
const ICON_LIGHT = "https://kargonode.com/wp-content/uploads/2026/03/logo-white-kargonode.webp";

const ACTIVE_STATES = new Set(["En Origen", "En Tránsito", "En Puerto"]);

function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { data: docTabs = [] } = useDocumentTypes();
  const { data: embarques = [] } = useEmbarques();
  const quota = useOrgQuota();
  const isDark = useIsDark();
  const location = useLocation();
  const { isSuperAdmin } = useIsSuperAdmin();

  const hasPro = ["PRO", "ENTERPRISE"].includes(quota.planType);
  const hasEnterprise = quota.planType === "ENTERPRISE";

  const activeCount = useMemo(
    () => embarques.filter((e) => ACTIVE_STATES.has(e.estado_logistico)).length,
    [embarques]
  );

  // Keep collapsible open when a doc-type route is active
  const docRouteActive = docTabs.some((tab) => location.pathname === `/dashboard/${tab.slug}`);

  return (
    <Sidebar collapsible="icon" className="border-r border-border bg-sidebar">
      <SidebarHeader className="p-4 border-b border-border space-y-3">
        {collapsed ? (
          <img src={isDark ? ICON_DARK : ICON_LIGHT} alt="KargoNode" className="h-8 w-8 mx-auto object-contain" />
        ) : (
          <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="KargoNode" className="h-8 object-contain" />
        )}
        <OrganizationSwitcher collapsed={collapsed} />
      </SidebarHeader>

      <SidebarContent className="pt-4">
        {/* Resumen Ejecutivo — destacado arriba */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink
                to="/dashboard/resumen"
                end
                className="hover:bg-sidebar-accent/50 transition-colors"
                activeClassName="bg-sidebar-accent text-primary font-medium"
              >
                <LayoutDashboard className="mr-2 h-4 w-4 shrink-0" />
                {!collapsed && <span>Resumen Ejecutivo</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* === Grupo: Operación === */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-4">
            {!collapsed && "Operación"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Torre de Control */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/dashboard/embarques"
                    end
                    className="hover:bg-primary/10 transition-colors"
                    activeClassName="bg-primary/15 text-primary font-semibold ring-1 ring-primary/20"
                  >
                    <Ship className="mr-2 h-5 w-5 shrink-0" strokeWidth={2.5} />
                    {!collapsed && (
                      <span className="flex items-center gap-2">
                        Torre de Control
                        {activeCount > 0 && (
                          <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-primary/15 text-primary font-bold">
                            {activeCount}
                          </Badge>
                        )}
                      </span>
                    )}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Simulador Pro-Forma */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/dashboard/simulador"
                    end
                    className="hover:bg-sidebar-accent/50 transition-colors"
                    activeClassName="bg-sidebar-accent text-primary font-medium"
                  >
                    <Sparkles className="mr-2 h-4 w-4 shrink-0" />
                    {!collapsed && <span>Simulador Pro-Forma</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* === Grupo: Gestión Documental === */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-4">
            {!collapsed && (
              <span className="flex items-center gap-1.5">
                <Database className="h-3 w-3" />
                Gestión Documental
              </span>
            )}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Collapsible: Tipos de Documentos */}
              <Collapsible defaultOpen={docRouteActive} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="hover:bg-sidebar-accent/50 transition-colors cursor-pointer">
                      <FolderTree className="mr-2 h-4 w-4 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1">Tipos de Documentos</span>
                          <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </>
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {docTabs.map((tab) => (
                        <SidebarMenuSubItem key={tab.slug}>
                          <SidebarMenuSubButton asChild>
                            <NavLink
                              to={`/dashboard/${tab.slug}`}
                              end
                              className="hover:bg-sidebar-accent/50 transition-colors"
                              activeClassName="bg-sidebar-accent text-primary font-medium"
                            >
                              <tab.icon className="mr-2 h-3.5 w-3.5 shrink-0" />
                              <span>{tab.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              {/* Directorio — fuera del collapsible */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/dashboard/directorio"
                    end
                    className="hover:bg-sidebar-accent/50 transition-colors"
                    activeClassName="bg-sidebar-accent text-primary font-medium"
                  >
                    <Building2 className="mr-2 h-4 w-4 shrink-0" />
                    {!collapsed && <span>Directorio</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Centro de Mando CFO — solo OWNER/ADMIN */}
              {["OWNER", "ADMIN"].includes(useAuth().userRole ?? "") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin/gastos-pendientes"
                      end
                      className="hover:bg-sidebar-accent/50 transition-colors"
                      activeClassName="bg-sidebar-accent text-primary font-medium"
                    >
                      <Wallet className="mr-2 h-4 w-4 shrink-0" />
                      {!collapsed && <span>Gastos Pendientes</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>


        {/* === Grupo: Sistema (super-admin) === */}
        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-4">
              {!collapsed && "Sistema"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin/performance"
                      end
                      className="hover:bg-sidebar-accent/50 transition-colors"
                      activeClassName="bg-sidebar-accent text-primary font-medium"
                    >
                      <Activity className="mr-2 h-4 w-4 shrink-0" />
                      {!collapsed && <span>DB Performance</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-2 space-y-1">
        <CreditUsage />
        <UserProfileMenu collapsed={collapsed} />
      </SidebarFooter>
    </Sidebar>
  );
}
